<template>
  <div class="statistics-page">
    <div class="statistics-heading">
      <h2 class="statistics-title">account statistics</h2>
      <modal-help topic="account-statistics" label="Help for this screen" show-label />
    </div>
    <p class="statistics-subtitle">How this server is using memory, processor, and the network, and the log of restarts.</p>
    <resource-notice />

    <p v-if="loadError" class="statistics-error">{{ loadError }}</p>
    <p v-else-if="!loaded" class="statistics-muted">loading statistics…</p>

    <div v-else class="statistics-body">
      <div class="summary-grid">
        <div class="summary-card">
          <div class="summary-value">{{ memoryTotal }}</div>
          <div class="summary-label">memory restarts</div>
          <div class="summary-meta">last {{ memoryLast }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-value">{{ updateTotal }}</div>
          <div class="summary-label">code updates</div>
          <div class="summary-meta">last {{ updateLast }}</div>
        </div>
      </div>

      <system-stats />

      <section class="log-section">
      <h3 class="log-title">restart log</h3>
      <p class="statistics-muted">Each row is a restart, or a group of restarts from the same day. Memory restarts and code updates are counted apart.</p>
      <p v-if="!entries.length" class="statistics-muted">This account has no recorded restarts.</p>
      <table v-else class="log-table">
        <thead>
          <tr>
            <th>when</th>
            <th>restarts</th>
            <th>reason</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(entry, index) in entries" :key="`${entry.at}-${index}`">
            <td>{{ formatWhen(entry.at) }}</td>
            <td>{{ entry.count }}</td>
            <td>{{ reasonLabel(entry.reason) }}</td>
          </tr>
        </tbody>
      </table>
      </section>
    </div>
  </div>
</template>

<script>

  import axios from 'axios'
  import { crashReasonLabel } from '../../../superturbodb_manager_server/modules/resource-crash.js'
  import ResourceNotice from './resource-notice.vue'
  import SystemStats from './system-stats.vue'

  export default {
    components: { ResourceNotice, SystemStats },
    data () {

      return {
        loaded: false,
        loadError: ``,
        memoryTotal: 0,
        memoryAt: ``,
        updateTotal: 0,
        updateAt: ``,
        entries: []
      }
    },
    computed: {
      memoryLast () {

        if (!this.memoryTotal) return `none`

        return formatCrashTime(this.memoryAt)
      },
      updateLast () {

        if (!this.updateTotal) return `none`

        return formatCrashTime(this.updateAt)
      }
    },
    mounted () {

      this.load()
    },
    methods: {
      async load () {

        this.loadError = ``

        try {

          const response = await axios.get(`${VUE_APP_APP_SERVER_URL}/account`, { withCredentials: true })
          const data = response.data || {}
          const count = Number(data.resourceCrashCount)
          const updates = Number(data.codeUpdateCount)
          const log = Array.isArray(data.resourceCrashLog) ? data.resourceCrashLog : []

          this.memoryTotal = Number.isFinite(count) && count > 0 ? count : 0
          this.memoryAt = data.resourceCrashAt || ``
          this.updateTotal = Number.isFinite(updates) && updates > 0 ? updates : 0
          this.updateAt = data.codeUpdateAt || ``
          this.entries = log.slice().reverse()
          this.loaded = true
        } catch (error) {

          this.loadError = `could not load account statistics`
        }
      },
      formatWhen (value) {

        return formatCrashTime(value)
      },
      reasonLabel (reason) {

        return crashReasonLabel(reason)
      }
    }
  }

  function formatCrashTime (value) {

    const date = new Date(value)

    if (!value || Number.isNaN(date.getTime())) return `time not recorded`

    return date.toLocaleString(undefined, { dateStyle: `medium`, timeStyle: `short` })
  }

</script>

<style scoped>

.statistics-page {
  padding: 28px 32px 48px;
  max-width: 1080px;
  overflow: auto;
  color: #e8e8e6;
}

.statistics-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 6px 0;
}

.statistics-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #e8e8e6;
  margin: 0;
}

.statistics-subtitle {
  font-size: 0.85rem;
  color: #707070;
  margin: 0 0 24px 0;
}

.statistics-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.summary-card {
  background-color: #141412;
  border: 1px solid #2a2a27;
  border-radius: 8px;
  padding: 16px 18px;
}

.summary-value {
  font-size: 1.6rem;
  font-weight: 600;
  color: #e8e8e6;
  line-height: 1.2;
}

.summary-when {
  font-size: 1rem;
  font-weight: 600;
}

.summary-label,
.summary-meta,
.log-title {
  font-size: 0.75rem;
  font-weight: 500;
  color: #8a8a86;
  letter-spacing: 0.03em;
  margin: 6px 0 0;
}

.summary-meta {
  color: #707070;
  font-weight: 400;
  letter-spacing: 0;
}

.log-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.log-title {
  margin: 8px 0 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #e8e8e6;
}

.statistics-muted {
  color: #707070;
  font-size: 0.78rem;
  margin: 0;
}

.statistics-error {
  color: #e05252;
  font-size: 0.78rem;
  margin: 0;
}

.log-table {
  width: 100%;
  border-collapse: collapse;
  background-color: #141412;
  border: 1px solid #2a2a27;
  border-radius: 8px;
  overflow: hidden;
}

.log-table th,
.log-table td {
  text-align: left;
  padding: 10px 14px;
  font-size: 0.8rem;
  border-bottom: 1px solid #2a2a27;
}

.log-table th {
  color: #8a8a86;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.log-table td {
  color: #e8e8e6;
}

.log-table tr:last-child td {
  border-bottom: 0;
}

</style>
